import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
// @ts-ignore
import { dataIdFromObject } from 'lib/apollo/cache/memory';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import RemindAPI from 'lib/RemindAPI';
import ACCEPT_COACHING_INVITATION from 'coaching/mutations/AcceptCoachingInvitation.gql';
import ADD_STUDENT_MUTATION from 'coaching/mutations/AddStudentToCoachingByDevice.gql';
import CREATE_COACH_MUTATION from 'coaching/mutations/CreateCoach.gql';
import CREATE_COACHING_RESERVATION_MUTATION from 'coaching/mutations/CreateCoachingReservation.gql';
import CREATE_COACHING_SESSION_MUTATION from 'coaching/mutations/CreateCoachingSession.gql';
import DELETE_COACHING_SESSION_MUTATION from 'coaching/mutations/DeleteCoachingSession.gql';
import GET_COACH_AVAILABILITY_FOR_EVENT_QUERY from 'coaching/queries/GetCoachAvailabilityForEvent.gql';
import GET_COACH_LIST_QUERY from 'coaching/queries/GetCoachList.gql';
import GET_NAME_FOR_USER_UUID_QUERY from 'coaching/queries/GetNameForUserUuid.gql';
import PATCH_COACH_MUTATION from 'coaching/mutations/PatchCoach.gql';
import PATCH_PARTICIPANT_MUTATION from 'coaching/mutations/PatchParticipant.gql';
import PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithConnectedStudent.gql';
import PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithNoConnectedStudent.gql';
import RESCHEDULE_COACHING_SESSION_INSTANCE_MUTATION from 'coaching/mutations/RescheduleCoachingSessionInstance.gql';
import {
  CoachingEventDefinitionInput,
  CoachingParticipantInput,
  CoachingCoachInput,
  CoachingSessionInput,
} from 'globalTypes';
import {
  AddStudentToCoachingByDevice,
  AddStudentToCoachingByDeviceVariables,
} from 'coaching/mutations/__generated__/AddStudentToCoachingByDevice';
import { CreateCoach, CreateCoachVariables } from 'coaching/mutations/__generated__/CreateCoach';
import {
  CreateCoachingReservation,
  CreateCoachingReservationVariables,
} from 'coaching/mutations/__generated__/CreateCoachingReservation';
import {
  CreateCoachingSession,
  CreateCoachingSessionVariables,
} from 'coaching/mutations/__generated__/CreateCoachingSession';
import {
  DeleteCoachingSession,
  DeleteCoachingSessionVariables,
} from 'coaching/mutations/__generated__/DeleteCoachingSession';
import {
  GetCoachAvailabilityForEvent,
  GetCoachAvailabilityForEventVariables,
} from 'coaching/queries/__generated__/GetCoachAvailabilityForEvent';
import {
  GetNameForUserUuid,
  GetNameForUserUuidVariables,
} from 'coaching/queries/__generated__/GetNameForUserUuid';
import {
  PatchParticipant,
  PatchParticipantVariables,
} from 'coaching/mutations/__generated__/PatchParticipant';
import { PatchCoach, PatchCoachVariables } from 'coaching/mutations/__generated__/PatchCoach';
import {
  PurchaseCoachingProductWithConnectedStudent,
  PurchaseCoachingProductWithConnectedStudentVariables,
  PurchaseCoachingProductWithConnectedStudent_purchaseCoachingSessionWithConnectedStudent,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithConnectedStudent';
import {
  PurchaseCoachingProductWithNoConnectedStudent,
  PurchaseCoachingProductWithNoConnectedStudentVariables,
  PurchaseCoachingProductWithNoConnectedStudent_purchaseCoachingSessionWithNoConnectedStudent,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithNoConnectedStudent';
import {
  RescheduleCoachingSessionInstance,
  RescheduleCoachingSessionInstanceVariables,
} from 'coaching/mutations/__generated__/RescheduleCoachingSessionInstance';

import { errorFragment } from 'queries/__generated__/errorFragment';
import { AcceptCoachingInvitation } from 'coaching/mutations/__generated__/AcceptCoachingInvitation';
import ToastActions from 'actions/ToastActions';
import { Student } from 'coaching/containers/GetProspectiveStudentOptionsContainer';

type PurchaseResult = {
  participantUuid: string | null;
  error?: { message: string; code?: string };
};

export function handleGQLError(error: errorFragment, consoleErrorText: string) {
  console.warn(error);
  console.error(`Coaching - ${consoleErrorText}`);
  ToastActions.showGQLError(error);
}

export function getCoachAvailabilityForEvent(
  coachUuid: string,
  eventDefinition: CoachingEventDefinitionInput,
  participantUuid: string
) {
  return RemindAPI.execute<GetCoachAvailabilityForEvent, GetCoachAvailabilityForEventVariables>({
    query: GET_COACH_AVAILABILITY_FOR_EVENT_QUERY,
    variables: {
      coachUuid,
      eventDefinition,
      participantUuid,
    },
    forceFetch: true,
  }).catch(err => {
    console.warn(err);
    console.error(
      `CoachingActions - could not get coach availability for event coach_uuid=${coachUuid} start_time=${eventDefinition.startDateTime} end_time=${eventDefinition.endDateTime} recurrence=${eventDefinition.recurrence}`
    );
  });
}

export function createCoachingSession(
  input: CoachingSessionInput,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const handleError = (e: Error | string) => {
    console.error(`CoachingActions - could not create coaching session. err=${e.toString()}`);
    ToastActions.showErrorToast(e.toString());
    return;
  };
  return RemindAPI.execute<CreateCoachingSession, CreateCoachingSessionVariables>({
    mutation: CREATE_COACHING_SESSION_MUTATION,
    variables: { input },
    refetchQueries,
  })
    .then(res => {
      const { createCoachingSession } = res;
      if (!createCoachingSession || createCoachingSession.error) {
        return handleError(createCoachingSession?.error?.message!);
      }
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(handleError);
}

export function rescheduleCoachingSessionInstance(
  coachingSessionInstanceUuid: string,
  newStartDateTime: string,
  newEndDateTime: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const handleError = (e: Error | string) => {
    console.error(
      `CoachingActions - could not reschedule coaching session instance. err=${e.toString()}`,
      { coachingSessionInstanceUuid, newStartDateTime, newEndDateTime }
    );
    ToastActions.showErrorToast(e.toString());
    return;
  };
  return RemindAPI.execute<
    RescheduleCoachingSessionInstance,
    RescheduleCoachingSessionInstanceVariables
  >({
    mutation: RESCHEDULE_COACHING_SESSION_INSTANCE_MUTATION,
    variables: { coachingSessionInstanceUuid, newStartDateTime, newEndDateTime },
    refetchQueries,
  })
    .then(res => {
      const { participantRescheduleCoachingSessionInstance } = res;
      if (
        !participantRescheduleCoachingSessionInstance ||
        participantRescheduleCoachingSessionInstance.error
      ) {
        return handleError(participantRescheduleCoachingSessionInstance?.error?.message!);
      }
      if (onSuccess) {
        onSuccess();
      }
      ToastActions.showSuccessToast('Session successfully rescheduled');
    })
    .catch(handleError);
}

export function deleteCoachingSession(
  coachingSessionUuid: string,
  cancellationReason?: string,
  coachingSessionInstanceUuid?: string,
  instanceStartDateTime?: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const errorText = `CoachingActions - could not delete session_uuid=${coachingSessionUuid} coaching_session_instance_uuid=${coachingSessionInstanceUuid}`;

  return RemindAPI.execute<DeleteCoachingSession, DeleteCoachingSessionVariables>({
    mutation: DELETE_COACHING_SESSION_MUTATION,
    variables: {
      coachingSessionUuid,
      instanceEventId: coachingSessionInstanceUuid,
      instanceStartDateTime,
      cancellationReason,
      coachingSessionInstanceUuid,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.deleteCoachingSession;
      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      onSuccess?.();
      ToastActions.showSuccessToast('Session successfully deleted');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not delete session');
    });
}

export function createNewCoach(userUuid: string, subjects: string[], hourlyRateInCents: number) {
  const errorText = `CoachingActions - could not create new coach userUuid=${userUuid} subjects=${subjects.join(
    ', '
  )}`;
  return RemindAPI.execute<CreateCoach, CreateCoachVariables>({
    mutation: CREATE_COACH_MUTATION,
    variables: {
      userUuid,
      subjects,
      hourlyRateInCents,
    },
    refetchQueries: [
      {
        query: GET_COACH_LIST_QUERY,
      },
    ],
  })
    .then(res => {
      const { error } = res.createCoach;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach successfully added');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not add coach');
    });
}

export function getNameForUserUuid(userUuid: string) {
  return RemindAPI.execute<GetNameForUserUuid, GetNameForUserUuidVariables>({
    query: GET_NAME_FOR_USER_UUID_QUERY,
    variables: {
      userUuid,
    },
  })
    .then(res => res.getNameForUserUuid)
    .catch(() => {
      ToastActions.showErrorToast('Could not find user’s name');
      return undefined;
    });
}

export function reserveCoachingSession(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  purchaserUuid: string,
  subject: string
): Promise<{ reservationUuid: string; expiresAt: string } | null> {
  const handleError = (e: Error | string) => {
    console.error(`CoachingActions - could not reserve coaching session. err=${e}`);
    ToastActions.showErrorToast(
      'There was a problem reserving your coaching session. Please try again.'
    );
    return null;
  };
  return RemindAPI.execute<CreateCoachingReservation, CreateCoachingReservationVariables>({
    mutation: CREATE_COACHING_RESERVATION_MUTATION,
    variables: { subject, purchaserUuid, startDateTime, endDateTime, coachUuid },
  })
    .then(res => {
      const { createCoachingReservation: reservation } = res;
      if (!reservation || reservation.error) {
        return handleError(reservation?.error?.message!);
      }
      return {
        reservationUuid: reservation.reservationUuid!,
        expiresAt: reservation.expiresAt!,
      };
    })
    .catch(handleError);
}

export function purchaseCoaching(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  tokenizedCard: string,
  purchaserUuid: string,
  timezone: string,
  subjects: string[],
  student: Partial<Student> & { name: string },
  studentGradeLevel: string,
  reservationUuid: string
): Promise<PurchaseResult> {
  const logError = (err: any, asError?: boolean): void => {
    const text = [
      `CoachingActions - could not purchase coaching purchaserUuid=${purchaserUuid}`,
      `studentUuid=${student.uuid} studentName=${student.name} coachUuid=${coachUuid}`,
      `startDateTime=${startDateTime} reservationUuid=${reservationUuid} error=${err}`,
    ].join(' ');

    if (asError) {
      console.error(text);
    } else {
      console.warn(text);
    }
  };

  const handlePurchaseResponse = (
    res:
      | PurchaseCoachingProductWithConnectedStudent_purchaseCoachingSessionWithConnectedStudent
      | PurchaseCoachingProductWithNoConnectedStudent_purchaseCoachingSessionWithNoConnectedStudent
  ): Promise<PurchaseResult> => {
    const { participantUuid, error: resError } = res;

    if (resError) {
      logError(resError);
    }

    return Promise.resolve({
      participantUuid,
      error: resError ? { message: resError.message, code: resError.code ?? undefined } : undefined,
    });
  };

  const sharedVars = {
    startDateTime,
    endDateTime,
    coachUuid,
    tokenizedCard,
    purchaserUuid,
    timezone,
    subjects,
    studentGradeLevel,
    reservationUuid,
  };

  const promise = student.uuid
    ? RemindAPI.execute<
        PurchaseCoachingProductWithConnectedStudent,
        PurchaseCoachingProductWithConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentUuid: student.uuid },
      }).then(res => handlePurchaseResponse(res.purchaseCoachingSessionWithConnectedStudent))
    : RemindAPI.execute<
        PurchaseCoachingProductWithNoConnectedStudent,
        PurchaseCoachingProductWithNoConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentName: student.name },
      }).then(res => handlePurchaseResponse(res.purchaseCoachingSessionWithNoConnectedStudent));

  return promise.catch(err => {
    logError(err, true);
    return {
      participantUuid: null,
      error: { message: 'We were unable to complete your coaching purchase' },
    };
  });
}

export function patchParticipant(uuid: string, participant: CoachingParticipantInput) {
  const errorText = `CoachingActions - could not create modify participant uuid=${uuid}`;
  return RemindAPI.execute<PatchParticipant, PatchParticipantVariables>({
    mutation: PATCH_PARTICIPANT_MUTATION,
    variables: {
      uuid,
      participant,
    },
  })
    .then(res => {
      const { error } = res.patchParticipant;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      updateParticipant(uuid, participant);
      ToastActions.showSuccessToast('Participant successfully modified');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not modify participant');
      return { success: false };
    });
}

export function patchCoach(uuid: string, coach: CoachingCoachInput) {
  const errorText = `CoachingActions - could not create modify coach uuid=${uuid}`;
  return RemindAPI.execute<PatchCoach, PatchCoachVariables>({
    mutation: PATCH_COACH_MUTATION,
    variables: {
      uuid,
      coach,
    },
  })
    .then(res => {
      const { error } = res.patchCoach;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      updateCoach(uuid, coach);
      ToastActions.showSuccessToast('Coach successfully modified');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not modify coach');
      return { success: false };
    });
}

export function addStudentByDevice(
  deviceAddress: string,
  participantUUID: string,
  userEmail?: string
) {
  const errorText = `CoachingActions - could not add student for participant=${participantUUID}`;
  return RemindAPI.execute<AddStudentToCoachingByDevice, AddStudentToCoachingByDeviceVariables>({
    mutation: ADD_STUDENT_MUTATION,
    variables: {
      deviceAddress,
      participantUUID,
    },
  })
    .then(res => {
      const { error } = res.addStudentToCoachingByDevice;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      ToastActions.showSuccessToast(
        `We’ve sent an invitation to ${deviceAddress}${userEmail &&
          ` and a confirmation to ${userEmail}`}`
      );
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast(
        'We were unable to add your student’s account at this time. Please email us at coaching@remind101.com for further assistance.'
      );
      return { success: false };
    });
}

export function acceptCoachingInvitation() {
  const errorText = 'CoachingActions - could not accept coaching invitation';

  return RemindAPI.execute<AcceptCoachingInvitation>({ mutation: ACCEPT_COACHING_INVITATION })
    .then(res => {
      const { error } = res.acceptCoachingInvitation;

      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return { success: false };
      }
      ToastActions.showSuccessToast('Coaching invitation was accepted');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('We were unable to confirm your invitation to coaching');
      return { success: false };
    });
}

function updateParticipant(uuid: string, participantInput: CoachingParticipantInput) {
  // updates a specific participant in the Apollo cache
  return getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'CoachingParticipant', uuid }),
    fragment: gql`
      fragment participant on CoachingParticipant {
        studentName
        studentUuid
        whiteboardUrl
      }
    `,
    variables: { uuid },
    data: {
      studentUuid: participantInput.studentUuid,
      studentName: participantInput.studentName,
      whiteboardUrl: participantInput.whiteboardUrl,
      __typename: 'CoachingParticipant',
    },
  });
}

function updateCoach(uuid: string, coachInput: CoachingCoachInput) {
  return getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'Coach', uuid }),
    fragment: gql`
      fragment coach on Coach {
        status
      }
    `,
    variables: { uuid },
    data: {
      status: coachInput.status,
      __typename: 'Coach',
    },
  });
}
